/** Pure utility functions for journal dates and titles. */

const JOURNAL_TITLE_MAX_LENGTH = 80;

/** Format a Date to "YYYY-MM-DD" in the user's local timezone. */
function formatDateKey(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/** Get today's date as "YYYY-MM-DD". */
export function todayKey(): string {
  return formatDateKey(new Date());
}

/**
 * Tidies a typed title for the entry's frontmatter: collapses whitespace,
 * trims, and caps it at 80 characters.
 *
 * Characters a file name cannot hold (":" in "Feb 3: Rain") are kept. The
 * title is what the user reads; only the stored path is made filename-safe,
 * and the server does that when it derives one.
 *
 * @param title - The title as typed.
 * @returns The title to store and show.
 */
export function normalizeTitle(title: string): string {
  const cleaned = title.replace(/\s+/g, ' ').trim();
  // Count code points, as the server's length cap does, so an emoji is never cut in half.
  const chars = Array.from(cleaned);
  return chars.length > JOURNAL_TITLE_MAX_LENGTH
    ? chars.slice(0, JOURNAL_TITLE_MAX_LENGTH).join('').trim()
    : cleaned;
}

/**
 * Resolve the target date for a forced-new journal entry.
 *
 * Precedence: an explicitly chosen date (from the New Entry date picker) wins;
 * otherwise the currently-open entry's own date; otherwise the current route
 * date. Returns undefined when none resolve.
 *
 * Takes the open entry's resolved date rather than its file path: two thirds of
 * the archive is filed as `YYYY/Month/Title.md`, so re-reading a date out of the
 * path found nothing and quietly fell through to the route date — which, since
 * selecting a sidebar row does not navigate, holds the previously-opened
 * entry's day rather than this one's.
 *
 * @param chosenDate - Date picked in the New Entry modal (YYYY-MM-DD), if any.
 * @param openEntryDate - The currently-open entry's date (YYYY-MM-DD), if any.
 * @param routeDate - The date from the URL route (YYYY-MM-DD), if any.
 */
export function resolveNewEntryDate(
  chosenDate: string | undefined,
  openEntryDate: string | undefined,
  routeDate: string | undefined
): string | undefined {
  if (chosenDate) return chosenDate;
  return openEntryDate || routeDate;
}

